'use client'

import { useState, useEffect, useRef } from 'react'
import {
  Check,
  RotateCcw,
  Palette,
  Type,
  Image as ImageIcon,
  Upload,
  Loader2,
  AlertCircle,
  Eye,
  Monitor,
  Smartphone,
  Square,
  Circle,
  Heart,
  X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme, type ThemeSettings } from '@/components/providers/theme-provider'

const DEFAULT_COLORS = {
  primaryColor: '#2C3E50',
  secondaryColor: '#8B7355',
  accentColor: '#D4AF37',
  backgroundColor: '#FAFAF8',
  textPrimaryColor: '#1A1A1A',
  textSecondaryColor: '#6B7280',
}

const COLOR_PRESETS = [
  {
    name: 'Klassisch',
    primary: '#2C3E50',
    secondary: '#8B7355',
    accent: '#D4AF37',
    bg: '#FAFAF8',
  },
  {
    name: 'Warm',
    primary: '#5D4037',
    secondary: '#A1887F',
    accent: '#FFAB40',
    bg: '#FFF8F0',
  },
  {
    name: 'Elegant',
    primary: '#1A237E',
    secondary: '#5C6BC0',
    accent: '#FFD700',
    bg: '#F5F5FF',
  },
  {
    name: 'Modern',
    primary: '#263238',
    secondary: '#607D8B',
    accent: '#00BCD4',
    bg: '#FAFAFA',
  },
  {
    name: 'Natur',
    primary: '#2E7D32',
    secondary: '#81C784',
    accent: '#FFD54F',
    bg: '#F1F8E9',
  },
  {
    name: 'Sanft',
    primary: '#4A5568',
    secondary: '#A0AEC0',
    accent: '#E2B97B',
    bg: '#F7FAFC',
  },
]

const HEADING_FONTS = [
  { id: 'playfair', name: 'Playfair Display', style: "'Playfair Display', serif" },
  { id: 'lora', name: 'Lora', style: "'Lora', serif" },
  { id: 'merriweather', name: 'Merriweather', style: "'Merriweather', serif" },
  { id: 'georgia', name: 'Georgia', style: "Georgia, serif" },
]

const BODY_FONTS = [
  { id: 'inter', name: 'Inter', style: "'Inter', sans-serif" },
  { id: 'open-sans', name: 'Open Sans', style: "'Open Sans', sans-serif" },
  { id: 'roboto', name: 'Roboto', style: "'Roboto', sans-serif" },
  { id: 'lato', name: 'Lato', style: "'Lato', sans-serif" },
  { id: 'source-sans', name: 'Source Sans 3', style: "'Source Sans 3', sans-serif" },
]

const BORDER_RADIUS_OPTIONS = [
  { id: 'none', name: 'Eckig', value: '0px' },
  { id: 'small', name: 'Leicht gerundet', value: '4px' },
  { id: 'medium', name: 'Gerundet', value: '8px' },
  { id: 'large', name: 'Stark gerundet', value: '16px' },
]

export default function AdminThemePage() {
  const { theme, setTheme, refreshTheme } = useTheme()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop')

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Local state for editing (before save)
  const [localTheme, setLocalTheme] = useState<ThemeSettings>(theme)

  useEffect(() => {
    setLocalTheme(theme)
    setLoading(false)
  }, [theme])

  // Apply local theme changes immediately for preview
  const updateLocalTheme = (updates: Partial<ThemeSettings>) => {
    const newTheme = { ...localTheme, ...updates }
    setLocalTheme(newTheme)
    setTheme(updates) // Apply to global theme for instant preview
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'logo')

      const res = await fetch('/api/admin/upload', {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Upload fehlgeschlagen')
      }

      const { url } = await res.json()
      updateLocalTheme({ logoUrl: url })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const res = await fetch('/api/admin/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logoUrl: localTheme.logoUrl,
          memorialPortalPrimaryColor: localTheme.primaryColor,
          memorialPortalSecondaryColor: localTheme.secondaryColor,
          memorialPortalAccentColor: localTheme.accentColor,
          memorialPortalBackgroundColor: localTheme.backgroundColor,
          memorialPortalTextPrimaryColor: localTheme.textPrimaryColor,
          memorialPortalTextSecondaryColor: localTheme.textSecondaryColor,
          headingFont: localTheme.headingFont,
          bodyFont: localTheme.bodyFont,
          borderRadius: localTheme.borderRadius,
        }),
      })

      if (!res.ok) throw new Error('Speichern fehlgeschlagen')

      await refreshTheme()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const resetToDefault = () => {
    updateLocalTheme({
      ...DEFAULT_COLORS,
      headingFont: 'playfair',
      bodyFont: 'inter',
      borderRadius: 'medium',
    })
  }

  const applyPreset = (preset: typeof COLOR_PRESETS[0]) => {
    updateLocalTheme({
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent,
      backgroundColor: preset.bg,
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Design & Branding</h1>
          <p className="text-gray-500 mt-1">
            Passen Sie Ihr Gedenkportal an Ihre Markenidentität an
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={resetToDefault}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Zurücksetzen</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: localTheme.primaryColor }}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {saving ? 'Speichert...' : saved ? 'Gespeichert!' : 'Speichern'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-3 space-y-6">
          {/* Logo Upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-5 h-5" style={{ color: localTheme.primaryColor }} />
              <h2 className="text-lg font-medium text-gray-900">Logo</h2>
            </div>

            <div className="flex items-start gap-6">
              <div
                className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: localTheme.backgroundColor }}
              >
                {localTheme.logoUrl ? (
                  <img
                    src={localTheme.logoUrl}
                    alt="Logo"
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                    <span className="text-xs">Kein Logo</span>
                  </div>
                )}
              </div>

              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-3">
                  Laden Sie Ihr Firmenlogo hoch. Empfohlen: PNG oder SVG mit transparentem Hintergrund.
                </p>
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploading ? 'Lädt...' : 'Logo hochladen'}
                  </button>
                  {localTheme.logoUrl && (
                    <button
                      onClick={() => updateLocalTheme({ logoUrl: null })}
                      className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Color Scheme */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5" style={{ color: localTheme.primaryColor }} />
              <h2 className="text-lg font-medium text-gray-900">Farbschema</h2>
            </div>

            {/* Presets */}
            <p className="text-sm text-gray-600 mb-3">Vordefinierte Farbschemata:</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className={cn(
                    'p-2 rounded-lg border-2 transition-all',
                    localTheme.primaryColor === preset.primary
                      ? 'border-gray-900 shadow-md'
                      : 'border-transparent hover:border-gray-200'
                  )}
                  title={preset.name}
                >
                  <div className="flex gap-0.5 mb-1">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                  </div>
                  <p className="text-xs text-gray-600 truncate">{preset.name}</p>
                </button>
              ))}
            </div>

            {/* Custom Colors */}
            <p className="text-sm text-gray-600 mb-3">Oder passen Sie die Farben individuell an:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'primaryColor', label: 'Primärfarbe', desc: 'Header, Buttons' },
                { key: 'secondaryColor', label: 'Sekundärfarbe', desc: 'Akzente, Links' },
                { key: 'accentColor', label: 'Akzentfarbe', desc: 'Highlights, Kerzen' },
                { key: 'backgroundColor', label: 'Hintergrund', desc: 'Seitenhintergrund' },
                { key: 'textPrimaryColor', label: 'Textfarbe', desc: 'Haupttext' },
                { key: 'textSecondaryColor', label: 'Text sekundär', desc: 'Beschreibungen' },
              ].map((color) => (
                <div key={color.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {color.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={localTheme[color.key as keyof ThemeSettings] as string}
                      onChange={(e) => updateLocalTheme({ [color.key]: e.target.value })}
                      className="w-10 h-10 flex-shrink-0 rounded cursor-pointer border border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={localTheme[color.key as keyof ThemeSettings] as string}
                        onChange={(e) => updateLocalTheme({ [color.key]: e.target.value })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm uppercase font-mono"
                      />
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{color.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Type className="w-5 h-5" style={{ color: localTheme.primaryColor }} />
              <h2 className="text-lg font-medium text-gray-900">Typografie</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Überschriften
                </label>
                <div className="space-y-2">
                  {HEADING_FONTS.map((font) => (
                    <button
                      key={font.id}
                      onClick={() => updateLocalTheme({ headingFont: font.id })}
                      className={cn(
                        'w-full text-left px-4 py-3 rounded-lg border-2 transition-all',
                        localTheme.headingFont === font.id
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <span
                        className="text-xl"
                        style={{ fontFamily: font.style }}
                      >
                        {font.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fließtext
                </label>
                <div className="space-y-2">
                  {BODY_FONTS.map((font) => (
                    <button
                      key={font.id}
                      onClick={() => updateLocalTheme({ bodyFont: font.id })}
                      className={cn(
                        'w-full text-left px-4 py-3 rounded-lg border-2 transition-all',
                        localTheme.bodyFont === font.id
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <span style={{ fontFamily: font.style }}>
                        {font.name} - Das ist ein Beispieltext
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Border Radius */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Square className="w-5 h-5" style={{ color: localTheme.primaryColor }} />
              <h2 className="text-lg font-medium text-gray-900">Ecken-Stil</h2>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {BORDER_RADIUS_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => updateLocalTheme({ borderRadius: option.id })}
                  className={cn(
                    'p-4 text-center rounded-lg border-2 transition-all',
                    localTheme.borderRadius === option.id
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div
                    className="w-12 h-12 mx-auto mb-2 border-2"
                    style={{
                      borderRadius: option.value,
                      borderColor: localTheme.primaryColor,
                      backgroundColor: localTheme.backgroundColor,
                    }}
                  />
                  <span className="text-sm text-gray-700">{option.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-4">
            {/* Preview Controls */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Live-Vorschau</span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    previewMode === 'desktop' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  )}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    previewMode === 'mobile' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  )}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Preview Card */}
            <div
              className={cn(
                'bg-white rounded-xl border border-gray-200 overflow-hidden transition-all',
                previewMode === 'mobile' ? 'max-w-[320px] mx-auto' : ''
              )}
            >
              {/* Preview Header */}
              <div
                className="p-4"
                style={{ backgroundColor: localTheme.primaryColor }}
              >
                <div className="flex items-center gap-3">
                  {localTheme.logoUrl ? (
                    <img
                      src={localTheme.logoUrl}
                      alt="Logo"
                      className="h-8 w-auto object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-white/20" />
                  )}
                  <span
                    className="text-white font-medium"
                    style={{ fontFamily: BODY_FONTS.find(f => f.id === localTheme.bodyFont)?.style }}
                  >
                    {localTheme.companyName}
                  </span>
                </div>
              </div>

              {/* Preview Content */}
              <div
                className="p-4"
                style={{ backgroundColor: localTheme.backgroundColor }}
              >
                {/* Mini Memorial Card */}
                <div
                  className="p-4 bg-white shadow-sm mb-4"
                  style={{
                    borderRadius: BORDER_RADIUS_OPTIONS.find(r => r.id === localTheme.borderRadius)?.value,
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: localTheme.secondaryColor + '20' }}
                    >
                      <Circle className="w-8 h-8" style={{ color: localTheme.secondaryColor }} />
                    </div>
                    <div>
                      <h3
                        className="font-medium"
                        style={{
                          fontFamily: HEADING_FONTS.find(f => f.id === localTheme.headingFont)?.style,
                          color: localTheme.textPrimaryColor,
                        }}
                      >
                        Max Mustermann
                      </h3>
                      <p
                        className="text-sm"
                        style={{
                          fontFamily: BODY_FONTS.find(f => f.id === localTheme.bodyFont)?.style,
                          color: localTheme.textSecondaryColor,
                        }}
                      >
                        * 1940 &nbsp; † 2024
                      </p>
                    </div>
                  </div>

                  <p
                    className="text-sm mb-3"
                    style={{
                      fontFamily: BODY_FONTS.find(f => f.id === localTheme.bodyFont)?.style,
                      color: localTheme.textSecondaryColor,
                    }}
                  >
                    In liebevoller Erinnerung an einen wunderbaren Menschen...
                  </p>

                  {/* Action Buttons Preview */}
                  <div className="flex gap-2">
                    <button
                      className="flex-1 py-2 px-3 text-sm text-white flex items-center justify-center gap-1"
                      style={{
                        backgroundColor: localTheme.accentColor,
                        borderRadius: BORDER_RADIUS_OPTIONS.find(r => r.id === localTheme.borderRadius)?.value,
                        fontFamily: BODY_FONTS.find(f => f.id === localTheme.bodyFont)?.style,
                      }}
                    >
                      <Heart className="w-4 h-4" />
                      Kerze
                    </button>
                    <button
                      className="flex-1 py-2 px-3 text-sm border flex items-center justify-center gap-1"
                      style={{
                        borderColor: localTheme.secondaryColor,
                        color: localTheme.secondaryColor,
                        borderRadius: BORDER_RADIUS_OPTIONS.find(r => r.id === localTheme.borderRadius)?.value,
                        fontFamily: BODY_FONTS.find(f => f.id === localTheme.bodyFont)?.style,
                      }}
                    >
                      Kondolenz
                    </button>
                  </div>
                </div>

                {/* Candles Preview */}
                <div className="flex justify-center gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="text-center">
                      <div
                        className="w-8 h-12 mx-auto mb-1 flex items-end justify-center"
                        style={{ color: localTheme.accentColor }}
                      >
                        <div className="relative">
                          <div
                            className="w-6 h-8 rounded-sm"
                            style={{
                              background: `linear-gradient(to bottom, ${localTheme.accentColor}, ${localTheme.accentColor}88)`,
                            }}
                          />
                          <div
                            className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-3 rounded-full animate-pulse"
                            style={{
                              background: `radial-gradient(ellipse at center, #FFE5A0 0%, ${localTheme.accentColor} 50%, transparent 70%)`,
                            }}
                          />
                        </div>
                      </div>
                      <span
                        className="text-xs"
                        style={{ color: localTheme.textSecondaryColor }}
                      >
                        Name
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Status */}
            {saved && (
              <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                <Check className="w-4 h-4" />
                Änderungen gespeichert! Die Seite verwendet jetzt Ihr neues Design.
              </div>
            )}

            {/* CSS Variables Info */}
            <div className="p-4 bg-gray-100 rounded-xl">
              <p className="text-sm font-medium text-gray-700 mb-2">Aktive CSS-Variablen</p>
              <code className="text-xs text-gray-600 block whitespace-pre font-mono">
{`--memorial-primary: ${localTheme.primaryColor}
--memorial-secondary: ${localTheme.secondaryColor}
--memorial-accent: ${localTheme.accentColor}
--memorial-background: ${localTheme.backgroundColor}`}
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
